/**
 * Conversaciones del chat de clientes (F3 — orden eb69c1df derivada de F2b/F2c).
 *
 * El backend (chat.ts, 24f47c3c) modela el hilo como una cadena causal GOBERNADA:
 *   conversacion.iniciada (contenedor) ⊃ consulta-ia.respondida (un turno) ⊃ informe-html.generado (§16, coreográfico).
 * El front NO crea ni transita nada de esto: solo LO LEE por endpoints canónicos y lo presenta
 * (frontera dual §IA-dual + §10: el front es consumidor, no emisor). La conversación se continúa
 * reenviando `conversacion_id` en el POST /chat — el back abre/continúa el hilo.
 *
 * Estos endpoints NO son /canonico/* — son superficies dedicadas del back, scopeadas por el CO
 * de sesión (RLS single-CO). Un 0/404 puede ser "no es de este CO", no ausencia.
 */
import { api } from './api'

/** Resumen de un hilo (GET /mis-conversaciones → data[]). */
export interface ConversacionResumen {
  id: string
  titulo?: string
  ultimo_mensaje_ts?: string
  n_consultas: number
  agente_id?: string
  created_at?: string
}

/** Un turno del hilo (consulta-ia.respondida). El transcript recuperable vive en pregunta/respuesta. */
export interface ConsultaTurno {
  id: string
  pregunta: string | null
  respuesta: string | null
  modelo?: string
  ts?: string
  /** El §16 nació un informe-html.generado disparado por esta consulta (trace_chain). */
  tiene_informe: boolean
}

/** Referencia a un informe coreográfico del hilo (informe-html.generado). El HTML NO viaja aquí:
 *  se lee aparte por el detalle canónico del informe (su html_resultado) — Inc 4. */
export interface InformeRef {
  id: string
  current_state: string
  created_at?: string
  /** trigger_id del informe = id de la consulta que lo disparó (§16). */
  consulta_id: string
}

/** Hilo cargado (GET /conversaciones/:id). */
export interface HiloConversacion {
  conversacion: {
    id: string
    titulo?: string
    current_state: string
    ultimo_mensaje_ts?: string
    n_consultas: number
    agente_id?: string
  }
  consultas: ConsultaTurno[]
  informes: InformeRef[]
}

/** Hilos ACTIVOS del usuario en su CO, más recientes primero. */
export function listarConversaciones(
  token: string | null,
  limit = 50,
): Promise<{ data: ConversacionResumen[]; meta: { total: number; limit?: number } }> {
  return api.get(`/mis-conversaciones?limit=${limit}`, token)
}

/** Carga un hilo: conversación + turnos (cronológicos) + informes nacidos de ellos. */
export function cargarConversacion(id: string, token: string | null): Promise<HiloConversacion> {
  return api.get(`/conversaciones/${encodeURIComponent(id)}`, token)
}

interface DetalleInstancia {
  data?: { attributes?: { payload?: Record<string, unknown> } }
}

/**
 * Lee el html_resultado del informe GOBERNADO (informe-html.generado) por su id, vía el detalle
 * canónico (la versión la reescribe api.ts contra el catálogo). Es la fuente de verdad del
 * informe coreográfico — no la copia inline del turno. Scopeado al CO (RLS); null si no hay html.
 */
export async function cargarHtmlInforme(informeId: string, token: string | null): Promise<string | null> {
  const r = await api.get<DetalleInstancia>(
    `/canonico/clientes/informe-html/generado/v1.4/instancias/${encodeURIComponent(informeId)}`,
    token,
  )
  const html = r.data?.attributes?.payload?.html_resultado
  return typeof html === 'string' && html.trim() ? html : null
}
